// OKLCH, where lightness is perceptual: the same lightness step is the same visible
// step for every hue (in HSL it barely changes a yellow and makes a lilac heavy).

/** Lightness 0–1, chroma, hue in degrees. */
export interface IOklch {
    l: number;
    c: number;
    h: number;
}

const hexPattern = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#787CDD", "787cdd" or "#abc" as [r, g, b] 0–255. Undefined for anything but hex. */
export const parseHex = (value: string): number[] | undefined => {
    const match = hexPattern.exec(value.trim());

    if (!match) {
        return undefined;
    }

    const hex = match[1].length === 3
        ? match[1].split("").map((ch) => ch + ch).join("")
        : match[1];

    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
};

const toLinear = (channel: number): number => {
    const c = channel / 255;

    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const fromLinear = (c: number): number =>
    c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;

/** A hex colour ("#787CDD", "787cdd", "#abc") as OKLCH. Undefined for anything but hex. */
export const hexToOklch = (value: string): IOklch | undefined => {
    const rgb = parseHex(value);

    if (!rgb) {
        return undefined;
    }

    const [r, g, b] = rgb.map(toLinear);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

    const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
    const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
    const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

    return { l: L, c: Math.hypot(A, B), h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
};

const oklchToLinearRgb = ({ l, c, h }: IOklch): number[] => {
    const a = c * Math.cos((h * Math.PI) / 180);
    const b = c * Math.sin((h * Math.PI) / 180);

    const L = Math.pow(l + 0.3963377774 * a + 0.2158037573 * b, 3);
    const M = Math.pow(l - 0.1055613458 * a - 0.0638541728 * b, 3);
    const S = Math.pow(l - 0.0894841775 * a - 1.291485548 * b, 3);

    return [
        4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
        -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
        -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
    ];
};

const inGamut = (rgb: number[]): boolean => rgb.every((v) => v >= -0.0001 && v <= 1.0001);

const toHexChannel = (linear: number): string =>
    Math.round(fromLinear(Math.min(1, Math.max(0, linear))) * 255)
        .toString(16)
        .padStart(2, "0");

/** OKLCH as hex. A colour outside sRGB loses chroma until it fits, so lightness and hue stay. */
export const oklchToHex = (colour: IOklch): string => {
    let c = colour.c;
    let rgb = oklchToLinearRgb(colour);

    while (!inGamut(rgb) && c > 0.0001) {
        c *= 0.97;
        rgb = oklchToLinearRgb({ ...colour, c });
    }

    return "#" + rgb.map(toHexChannel).join("");
};
